// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import StackedRadiosGroup from 'components/atoms/StackedRadiosGroup';
import { PrimaryTemplate, PrimaryTemplateProps } from '../PrimaryTemplate';
import IconList from 'components/atoms/icons/IconList';
import IconMapView from 'components/atoms/icons/IconMapView';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './CollectRouteTemplate.module.scss';
import { getApiPath, mergeClasses, objectFetch } from 'util/commons';
import ButtonLoadingVolume from 'components/molecules/ButtonLoadingVolume';
import CollectRouteMapView from 'components/organisms/CollectRouteMapView';
import { useQuery, useUpdateQuery } from 'util/queryParams';
import { useTranslation } from 'react-i18next';
import { IconClose } from 'components/atoms/icons/IconClose';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import LoadingVolumeModal from 'components/molecules/LoadingVolumeModal';
import { useHistory, useLocation, useParams } from 'react-router';
import { getCollectRouteSites } from 'apis/collect_route';
import { Path } from 'routes';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetCollectingRouteSites,
  selectCollectingRouteSites,
  setCollectingRouteSites,
} from 'redux/slices/collectingRoute';
import { CollectRouteSiteList } from 'types/routeManagementTypes';
import { StorageKeys } from 'util/Enums';
import { CollectingRouteSession } from 'util/collectingRoute';

type CollectRouteTemplateProps = Omit<
  PrimaryTemplateProps,
  'className' | 'children'
> & {
  classNames?: {
    wrapper?: string;
    actions?: string;
  };
  children?: React.ReactNode;
  showViewModeSwitch?: boolean;
  leftComponent?: React.ReactNode;
  showOnlySavedSelectedSiteOnMap?: boolean;
  checkNoneRouteId?: boolean;
};

const CollectRouteTemplate: React.FC<CollectRouteTemplateProps> = ({
  children,
  classNames,
  showViewModeSwitch = true,
  leftComponent,
  showOnlySavedSelectedSiteOnMap,
  checkNoneRouteId,
  ...props
}) => {
  const { t } = useTranslation();
  const { routeId, siteId } = useParams() as {
    routeId?: string;
    siteId?: string;
  };
  const { state } = useLocation();
  const dispatch = useDispatch();
  const rawCollectingRouteSites = useSelector(selectCollectingRouteSites);
  const [savedSelectedSites, setSavedSelectedSites] = useState<
    Record<string, boolean>
  >({});
  const [canRender, setCanRender] = useState(false);

  useEffect(() => {
    if (checkNoneRouteId && routeId === 'none') {
      history.replace(`${Path.routeList}${window.location.search}`);
    } else {
      setCanRender(true);
    }
  }, [routeId, checkNoneRouteId]);

  useEffect(() => {
    if (!showOnlySavedSelectedSiteOnMap || !routeId) {
      setSavedSelectedSites({});

      return;
    }

    const savedData = CollectingRouteSession.get(routeId)?.siteMap || {};

    setSavedSelectedSites(savedData);
  }, [showOnlySavedSelectedSiteOnMap, routeId]);

  const collectingRouteSites: CollectRouteSiteList | null = useMemo(() => {
    if (!rawCollectingRouteSites) {
      return null;
    }

    if (
      !Object.keys(savedSelectedSites).filter((key) => savedSelectedSites[key])
        .length
    ) {
      return rawCollectingRouteSites;
    }

    const filteredSites = rawCollectingRouteSites.sites.filter(
      (site) => savedSelectedSites[site.id]
    );

    if (!filteredSites.length) {
      return rawCollectingRouteSites;
    }

    return {
      ...rawCollectingRouteSites,
      sites: filteredSites,
    };
  }, [savedSelectedSites, rawCollectingRouteSites]);

  const history = useHistory();

  const queryParams = useQuery();
  const keyword = queryParams.get('mapKeyword') || undefined;
  const [viewMode, setViewMode] = useState<'list' | 'map'>(
    showViewModeSwitch &&
      localStorage.getItem(StorageKeys.COLLECT_MAP_VIEW_MODE_STORAGE_KEY) ===
        'map'
      ? 'map'
      : 'list'
  );
  const [showPage, setShowPage] = useState(!routeId || !!collectingRouteSites);
  const [openLoadingModal, setOpenLoadingModal] = useState(false);
  const updateQueryParams = useUpdateQuery();
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (routeId) {
      if (
        !collectingRouteSites ||
        (state && objectFetch(state, 'refreshSites'))
      ) {
        getCollectRouteSites({
          routeId,
          successCallback: (sites) => {
            dispatch(setCollectingRouteSites(sites));

            if (siteId && sites.sites.every((site) => site.id !== siteId)) {
              history.replace(
                `${getApiPath(Path.routeSiteSelection, { routeId })}${
                  window.location.search
                }`
              );
            }
          },
          failedCallback: () => {
            dispatch(resetCollectingRouteSites());
            history.replace(Path.routeList);
          },
          finallyCallback: () => {
            setShowPage(true);
          },
        });
      }
    } else {
      dispatch(resetCollectingRouteSites());
    }
  }, [routeId, state]);

  const handleUpdateViewMode = (mode: 'list' | 'map') => {
    setViewMode(mode);
    localStorage.setItem(StorageKeys.COLLECT_MAP_VIEW_MODE_STORAGE_KEY, mode);
  };

  const handleMapSearch = (keyword: string) => {
    updateQueryParams({
      callback: (searchParams) => {
        if (keyword) {
          searchParams.set('mapKeyword', keyword);
        } else {
          searchParams.delete('mapKeyword');
        }
      },
    });
  };

  if (!canRender) {
    return null;
  }

  return (
    <PrimaryTemplate
      {...props}
      className={mergeClasses(
        styles.wrapper,
        {
          [styles.wrapperWithMapView]: viewMode === 'map',
        },
        classNames?.wrapper
      )}
      keepSearchParamsOnPrev
    >
      <div
        className={mergeClasses(styles.viewModeAndButtonContainer, {
          [styles.viewModeAndButtonContainerWithMapView]: viewMode === 'map',
        })}
      >
        {viewMode === 'map' && collectingRouteSites?.routeName && (
          <div className={styles.currentRouteName}>
            <span className={styles.routeNameLabel}>
              <span className={styles.label}>
                {t('route_list.thead_route_name')}:
              </span>{' '}
              {collectingRouteSites.routeName}
            </span>
          </div>
        )}

        <div
          className={mergeClasses(
            styles.actions,
            {
              [styles.actionsWithMapView]: viewMode === 'map',
            },
            classNames?.actions
          )}
        >
          <div
            className={mergeClasses(styles.viewModeAndReturnButton, {
              [styles.viewModeAndReturnButtonWithMapView]: viewMode === 'map',
            })}
          >
            {showViewModeSwitch && !leftComponent && (
              <StackedRadiosGroup
                classNames={{
                  wrapper: styles.viewMode,
                  item: styles.mapChangeViewItem,
                  active: styles.mapChangeViewItemActive,
                }}
                options={[
                  { label: <IconList />, value: 'list' },
                  { label: <IconMapView />, value: 'map' },
                ]}
                onChange={(val) => handleUpdateViewMode(val as 'list' | 'map')}
                value={viewMode}
              />
            )}

            {leftComponent && !showViewModeSwitch && <div>{leftComponent}</div>}

            {viewMode === 'map' && (
              <form
                className={styles.searchInput}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleMapSearch(searchInputRef.current?.value.trim() || '');
                  if (searchInputRef.current) {
                    searchInputRef.current.value =
                      searchInputRef.current.value.trim();
                  }
                }}
              >
                <div className={styles.searchInputContent}>
                  <input
                    name="keyword"
                    placeholder={t('route_list.search_map_placeholder') || ''}
                    ref={searchInputRef}
                    defaultValue={keyword}
                    className={mergeClasses(styles.searchInputField, {
                      [styles.hasClear]: !!keyword,
                    })}
                  />
                  {keyword && (
                    <span
                      className={styles.searchClear}
                      onClick={() => {
                        handleMapSearch('');
                        if (searchInputRef.current) {
                          searchInputRef.current.value = '';
                        }
                      }}
                    >
                      <IconClose strokeColor="#e7e7e7" width={12} height={12} />
                    </span>
                  )}
                </div>

                <button type="submit">
                  <IconSearch color="black" />
                </button>
              </form>
            )}
          </div>

          <div>
            <ButtonLoadingVolume
              handleOnClick={() => setOpenLoadingModal(true)}
            />
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        showPage && <div className={styles.childrenContainer}>{children}</div>
      ) : (
        <CollectRouteMapView
          currentRouteSites={collectingRouteSites || undefined}
        />
      )}

      {openLoadingModal && (
        <LoadingVolumeModal onClose={() => setOpenLoadingModal(false)} />
      )}
    </PrimaryTemplate>
  );
};

export default CollectRouteTemplate;
